import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiSettings,
  FiX,
  FiEdit3,
  FiUsers,
  FiUserPlus,
  FiUserMinus,
  FiTrash2,
  FiLogOut,
  FiCheck,
  FiAlertTriangle,
  FiSave,
  FiRefreshCw,
  FiChevronDown,
  FiMessageCircle,
  FiClock,
  FiLoader,
  FiInfo,
  FiImage,
  FiUpload,
} from "react-icons/fi";
import { useRoom } from "../../../context/RoomContext";
import { UserContext } from "../../../context/UserContext";
import roomService from "../../../services/rooms/roomService";
import { roomPhotoService } from "../../../services/rooms";
import { formatTimeAgo, getFieldImage } from "../../../utils/roomHelpers";
import { toast } from "react-toastify";
import "./RoomSettingsModal.css";

// ==============================================
// Animation Variants
// ==============================================
const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 30 },
  },
  exit: { opacity: 0, scale: 0.95, y: 20, transition: { duration: 0.15 } },
};

const sectionVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
};

// ==============================================
// Sub-Components
// ==============================================

// Helper: Generate initials from name
const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Avatar colors based on initials
const getAvatarColor = (name) => {
  const colors = [
    { bg: '#dbeafe', text: '#3b82f6' }, // Blue
    { bg: '#d1fae5', text: '#10b981' }, // Green
    { bg: '#fef3c7', text: '#f59e0b' }, // Amber
    { bg: '#e0e7ff', text: '#6366f1' }, // Indigo
    { bg: '#cffafe', text: '#06b6d4' }, // Cyan
    { bg: '#fce7f3', text: '#ec4899' }, // Pink
  ];
  const index = name ? name.charCodeAt(0) % colors.length : 0;
  return colors[index];
};

// Avatar Component with Initials Fallback
const Avatar = ({ src, name, className, size = 'md' }) => {
  const [imageError, setImageError] = useState(false);
  const hasValidImage = src && !imageError;
  const initials = getInitials(name);
  const colors = getAvatarColor(name || '');

  const sizeClasses = {
    sm: 'rrsm-avatar--sm',
    md: 'rrsm-avatar--md',
    lg: 'rrsm-avatar--lg',
  };

  if (hasValidImage) {
    return (
      <img
        src={src}
        alt={name || 'User'}
        className={`${className} ${sizeClasses[size]}`}
        onError={() => setImageError(true)}
      />
    );
  }

  return (
    <div 
      className={`rrsm-avatar-initials ${className} ${sizeClasses[size]}`}
      style={{ backgroundColor: colors.bg, color: colors.text }}
      title={name || 'Unknown User'}
    >
      {initials}
    </div>
  );
};

// Section Header
const SectionHeader = ({ icon: Icon, title, badge, color = "blue" }) => (
  <div className="rrsm-section-header" data-color={color}>
    <div className="rrsm-section-icon">
      <Icon />
    </div>
    <h3 className="rrsm-section-title">{title}</h3>
    {badge !== undefined && <span className="rrsm-section-badge">{badge}</span>}
  </div>
);

// Form Input
const FormInput = ({ label, error, ...props }) => (
  <div className="rrsm-form-group">
    <label className="rrsm-label">{label}</label>
    <input className={`rrsm-input ${error ? "rrsm-input--error" : ""}`} {...props} />
    {error && <span className="rrsm-error-text">{error}</span>}
  </div>
);

// Form Textarea
const FormTextarea = ({ label, error, charCount, maxLength, ...props }) => (
  <div className="rrsm-form-group">
    <label className="rrsm-label">{label}</label>
    <textarea className={`rrsm-textarea ${error ? "rrsm-textarea--error" : ""}`} {...props} />
    <div className="rrsm-field-footer">
      {error && <span className="rrsm-error-text">{error}</span>}
      {charCount !== undefined && (
        <span className="rrsm-char-count">{charCount}/{maxLength}</span>
      )}
    </div>
  </div>
);

// Form Select
const FormSelect = ({ label, options, placeholder, ...props }) => (
  <div className="rrsm-form-group">
    <label className="rrsm-label">{label}</label>
    <div className="rrsm-select-wrapper">
      <select className="rrsm-select" {...props}>
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      <FiChevronDown className="rrsm-select-icon" />
    </div>
  </div>
);

// Member Card
const MemberCard = ({ member, isLeader, canRemove, onRemove, loading }) => {
  const memberUser = member.user || member;
  const memberId = memberUser._id || memberUser.id;
  const memberName = memberUser.name || 'Unknown';
  
  return (
    <div className="rrsm-member-card">
      <Avatar
        src={memberUser.profilePhotoUrl}
        name={memberName}
        className="rrsm-member-avatar"
        size="md"
      />
      <div className="rrsm-member-info">
        <div className="rrsm-member-name">
          {memberName}
          {isLeader && <span className="rrsm-leader-badge">Leader</span>}
        </div>
        {member.joinedAt && (
          <div className="rrsm-member-meta">
            <FiClock />
            <span>Joined {formatTimeAgo(member.joinedAt)}</span>
          </div>
        )}
      </div>
      {canRemove && (
        <motion.button
          className="rrsm-member-remove"
          onClick={() => onRemove(memberId, memberName)}
          disabled={loading}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title="Remove member"
        >
          <FiUserMinus />
        </motion.button>
      )}
    </div>
  );
};

// Join Request Card
const JoinRequestCard = ({ request, onAccept, onReject, loading, isRoomFull }) => {
  const requestUser = request.user || {};
  const requestId = requestUser._id || requestUser.id;
  const requestName = requestUser.name || 'Unknown';

  return (
    <div className="rrsm-request-card">
      <div className="rrsm-request-header">
        <Avatar
          src={requestUser.profilePhotoUrl}
          name={requestName}
          className="rrsm-request-avatar"
          size="md"
        />
        <div className="rrsm-request-info">
          <div className="rrsm-request-name">{requestName}</div>
          {requestUser.professionalRole && (
            <div className="rrsm-request-role">{requestUser.professionalRole}</div>
          )}
          {requestUser.field && (
            <div className="rrsm-request-field">{requestUser.field}</div>
          )}
        </div>
        <div className="rrsm-request-time">
          <FiClock />
          <span>{formatTimeAgo(request.requestedAt || request.createdAt)}</span>
        </div>
      </div>
      
      {request.message && (
        <div className="rrsm-request-message">
          <FiMessageCircle className="rrsm-message-icon" />
          <div className="rrsm-message-content">
            <span className="rrsm-message-label">Message from applicant:</span>
            <p className="rrsm-message-text">"{request.message}"</p>
          </div>
        </div>
      )}

      {requestUser.interests && requestUser.interests.length > 0 && (
        <div className="rrsm-request-interests">
          {requestUser.interests.slice(0, 4).map((interest, idx) => (
            <span key={idx} className="rrsm-interest-tag">{interest}</span>
          ))}
        </div>
      )}

      <div className="rrsm-request-actions">
        <motion.button
          className="rrsm-btn-reject"
          onClick={() => onReject(requestId)}
          disabled={loading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FiX />
          <span>Reject</span>
        </motion.button>
        <motion.button
          className="rrsm-btn-accept"
          onClick={() => onAccept(requestId)}
          disabled={loading || isRoomFull}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {loading ? (
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              style={{ display: "inline-flex" }}
            >
              <FiLoader />
            </motion.span>
          ) : (
            <FiCheck />
          )}
          <span>{isRoomFull ? "Lab Full" : "Accept"}</span>
        </motion.button>
      </div>
    </div>
  );
};

// Confirmation Modal
const ConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "danger",
  requireInput,
  inputPlaceholder,
  loading
}) => {
  const [inputValue, setInputValue] = useState("");
  
  const handleConfirm = () => {
    if (requireInput && inputValue !== requireInput) return;
    onConfirm();
  };

  useEffect(() => {
    if (!isOpen) setInputValue("");
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="rrsm-confirm-overlay"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={onClose}
        >
          <motion.div
            className="rrsm-confirm-modal"
            variants={modalVariants}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`rrsm-confirm-icon rrsm-confirm-icon--${variant}`}>
              <FiAlertTriangle />
            </div>
            <h3 className="rrsm-confirm-title">{title}</h3>
            <p className="rrsm-confirm-message">{message}</p>
            
            {requireInput && (
              <div className="rrsm-confirm-input-group">
                <label>Type <strong>"{requireInput}"</strong> to confirm:</label>
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={inputPlaceholder || `Type "${requireInput}"`}
                  className="rrsm-confirm-input"
                />
              </div>
            )}

            <div className="rrsm-confirm-actions">
              <motion.button
                className="rrsm-btn-cancel"
                onClick={onClose}
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {cancelText}
              </motion.button>
              <motion.button
                className={`rrsm-btn-confirm rrsm-btn-confirm--${variant}`}
                onClick={handleConfirm}
                disabled={loading || (requireInput && inputValue !== requireInput)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {loading ? (
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    style={{ display: "inline-flex" }}
                  >
                    <FiLoader />
                  </motion.span>
                ) : null}
                <span>{confirmText}</span>
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ==============================================
// Main Component
// ==============================================
const RoomSettingsModal = ({ isOpen, onClose, room, userRole }) => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { 
    handleJoinRequest, 
    removeMember, 
    updateRoom,
    leaveRoom,
    deleteRoom,
    fetchRoomById,
    loading 
  } = useRoom();

  // State
  const [activeTab, setActiveTab] = useState("general");
  const [fields, setFields] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    field: "",
    subField: "",
    photoURL: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Confirmation modals state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: null,
    data: null,
  });

  const isLeader = userRole?.isLeader || false;
  const userId = user?.id || user?._id;
  const memberCount = room?.members?.length || 0;
  const maxMembers = room?.maxMembers || 8;
  const isRoomFull = memberCount >= maxMembers;

  // Photo upload state
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoDeleting, setPhotoDeleting] = useState(false);
  const photoInputRef = useRef(null);

  // Load fields on mount
  useEffect(() => {
    const loadFields = async () => {
      try {
        const response = await roomService.getResearchFields();
        if (response.success) {
          setFields(response.data || {});
        }
      } catch (err) { /* ignored */ }
    };
    loadFields();
  }, []);

  // Initialize form data when room changes
  useEffect(() => {
    if (room) {
      setFormData({
        name: room.name || "",
        description: room.description || "",
        field: room.field || "",
        subField: room.subField || "",
        photoURL: room.photoURL || "",
      });
      setHasChanges(false);
      setFormErrors({});
    }
  }, [room]);

  // Reset tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(isLeader ? "general" : "membership");
    }
  }, [isOpen, isLeader]);

  // Form handlers
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    
    // Clear error when user types
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: null }));
    }

    // Reset subField when field changes
    if (field === "field") {
      setFormData((prev) => ({ ...prev, subField: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (formData.name.length < 15 || formData.name.length > 100) {
      errors.name = "Name must be 15-100 characters";
    }
    if (formData.description.length < 30 || formData.description.length > 500) {
      errors.description = "Description must be 30-500 characters";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      await updateRoom(room._id, formData);
      setHasChanges(false);
      await fetchRoomById(room._id);
    } catch (err) { /* ignored */ } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (room) {
      setFormData({
        name: room.name || "",
        description: room.description || "",
        field: room.field || "",
        subField: room.subField || "",
        photoURL: room.photoURL || "",
      });
      setHasChanges(false);
      setFormErrors({});
    }
  };

  // Photo upload handler
  const handlePhotoUpload = useCallback(async (file) => {
    if (!file || !room?._id) return;

    // Validate file
    const validation = roomPhotoService.validateFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    setPhotoUploading(true);
    try {
      await roomPhotoService.uploadRoomPhoto(room._id, file);
      toast.success("Lab photo updated successfully!");
      await fetchRoomById(room._id);
    } catch (err) {
      toast.error(err.message || "Failed to upload photo");
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) {
        photoInputRef.current.value = "";
      }
    }
  }, [room?._id, fetchRoomById]);

  // Photo delete handler
  const handlePhotoDelete = useCallback(async () => {
    if (!room?._id) return;

    setPhotoDeleting(true);
    try {
      await roomPhotoService.deleteRoomPhoto(room._id);
      toast.success("Lab photo removed successfully!");
      await fetchRoomById(room._id);
    } catch (err) {
      toast.error(err.message || "Failed to delete photo");
    } finally {
      setPhotoDeleting(false);
    }
  }, [room?._id, fetchRoomById]);

  // Action handlers
  const handleAcceptRequest = async (requestUserId) => {
    setActionLoading(true);
    try {
      await handleJoinRequest(room._id, requestUserId, "accept");
      await fetchRoomById(room._id);
    } catch (err) { /* ignored */ } finally {
      setActionLoading(false);
    }
  };

  const handleRejectRequest = async (requestUserId) => {
    setActionLoading(true);
    try {
      await handleJoinRequest(room._id, requestUserId, "reject");
      await fetchRoomById(room._id);
    } catch (err) { /* ignored */ } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveMember = (memberId, memberName) => {
    setConfirmModal({
      isOpen: true,
      type: "remove",
      data: { memberId, memberName },
    });
  };

  const confirmRemoveMember = async () => {
    const { memberId } = confirmModal.data;
    setActionLoading(true);
    try {
      await removeMember(room._id, memberId);
      await fetchRoomById(room._id);
      setConfirmModal({ isOpen: false, type: null, data: null });
    } catch (err) { /* ignored */ } finally {
      setActionLoading(false);
    }
  };

  const handleLeaveRoom = () => {
    setConfirmModal({
      isOpen: true,
      type: "leave",
      data: null,
    });
  };

  const confirmLeaveRoom = async () => {
    setActionLoading(true);
    try {
      await leaveRoom(room._id);
      setConfirmModal({ isOpen: false, type: null, data: null });
      onClose();
      navigate("/labs");
    } catch (err) { /* ignored */ } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteRoom = () => {
    setConfirmModal({
      isOpen: true,
      type: "delete",
      data: null,
    });
  };

  const confirmDeleteRoom = async () => {
    setActionLoading(true);
    try {
      await deleteRoom(room._id);
      setConfirmModal({ isOpen: false, type: null, data: null });
      onClose();
      navigate("/labs");
    } catch (err) { /* ignored */ } finally {
      setActionLoading(false);
    }
  };

  const closeConfirmModal = () => {
    setConfirmModal({ isOpen: false, type: null, data: null });
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Get subfields for selected field
  const subFields = formData.field && fields[formData.field] 
    ? fields[formData.field] 
    : [];

  // Get member join date
  const getMemberJoinDate = () => {
    if (!room?.members || !userId) return null;
    const membership = room.members.find(
      (m) => (m.user?._id || m.user?.id) === userId
    );
    return membership?.joinedAt;
  };

  if (!room) return null;

  // Render modal using Portal to avoid parent container clipping
  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="rrsm-overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={handleOverlayClick}
          >
            <motion.div
              className="rrsm-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="rrsm-header">
                <div className="rrsm-header-content">
                  <div className="rrsm-header-icon">
                    <FiSettings />
                  </div>
                  <div className="rrsm-header-text">
                    <h2 className="rrsm-title">Lab Settings</h2>
                    <p className="rrsm-subtitle">{room.name}</p>
                  </div>
                </div>
                <div className="rrsm-header-right">
                  <span className={`rrsm-role-badge ${isLeader ? "rrsm-role-badge--leader" : ""}`}>
                    {isLeader ? "Leader" : "Member"}
                  </span>
                  <motion.button
                    className="rrsm-close-btn"
                    onClick={onClose}
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FiX />
                  </motion.button>
                </div>
              </div>

              {/* Tab Navigation (Leader only) */}
              {isLeader && (
                <div className="rrsm-tabs">
                  <button
                    className={`rrsm-tab ${activeTab === "general" ? "rrsm-tab--active" : ""}`}
                    onClick={() => setActiveTab("general")}
                  >
                    <FiEdit3 />
                    <span>General</span>
                  </button>
                  <button
                    className={`rrsm-tab ${activeTab === "members" ? "rrsm-tab--active" : ""}`}
                    onClick={() => setActiveTab("members")}
                  >
                    <FiUsers />
                    <span>Members</span>
                    <span className="rrsm-tab-badge">{memberCount}/{maxMembers}</span>
                  </button>
                  {room.joinRequests?.length > 0 && (
                    <button
                      className={`rrsm-tab ${activeTab === "requests" ? "rrsm-tab--active" : ""}`}
                      onClick={() => setActiveTab("requests")}
                    >
                      <FiUserPlus />
                      <span>Requests</span>
                      <span className="rrsm-tab-badge rrsm-tab-badge--highlight">
                        {room.joinRequests.length}
                      </span>
                    </button>
                  )}
                  <button
                    className={`rrsm-tab rrsm-tab--danger ${activeTab === "danger" ? "rrsm-tab--active" : ""}`}
                    onClick={() => setActiveTab("danger")}
                  >
                    <FiTrash2 />
                    <span>Danger</span>
                  </button>
                </div>
              )}

              {/* Body */}
              <div className="rrsm-body">
                {/* ====================== */}
                {/* LEADER VIEW */}
                {/* ====================== */}
                {isLeader && (
                  <>
                    {/* General Tab */}
                    {activeTab === "general" && (
                      <motion.div
                        className="rrsm-section"
                        variants={sectionVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <SectionHeader icon={FiEdit3} title="Edit Lab Information" color="blue" />
                        
                        <div className="rrsm-form">
                          <FormInput
                            label="Lab Name"
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange("name", e.target.value)}
                            placeholder="Enter lab name (15-100 characters)"
                            error={formErrors.name}
                          />
                          
                          <FormTextarea
                            label="Description"
                            value={formData.description}
                            onChange={(e) => handleInputChange("description", e.target.value)}
                            placeholder="Describe your research lab (30-500 characters)"
                            rows={4}
                            error={formErrors.description}
                            charCount={formData.description.length}
                            maxLength={500}
                          />

                          <div className="rrsm-form-row">
                            <FormSelect
                              label="Field"
                              value={formData.field}
                              onChange={(e) => handleInputChange("field", e.target.value)}
                              options={Object.keys(fields)}
                              placeholder="Select field"
                            />
                            <FormSelect
                              label="Sub-Field"
                              value={formData.subField}
                              onChange={(e) => handleInputChange("subField", e.target.value)}
                              options={subFields}
                              placeholder="Select sub-field"
                              disabled={!formData.field}
                            />
                          </div>

                          <div className="rrsm-form-group">
                            <label className="rrsm-label">
                              <FiImage style={{ marginRight: 8 }} />
                              Lab Photo
                            </label>
                            
                            <div className="rrsm-photo-section">
                              {/* Photo Preview */}
                              <div className="rrsm-photo-preview-container">
                                <img
                                  src={room.photoToUse || room.signedProfilePhotoUrl || room.photoURL || getFieldImage(room.field)}
                                  alt="Lab photo"
                                  className="rrsm-photo-preview-img"
                                  onError={(e) => {
                                    e.target.src = room.photoURL || getFieldImage(room.field) || "/field-images/default.jpg";
                                  }}
                                />
                                
                                {/* Custom photo badge */}
                                {room.profilePhoto && (
                                  <div className="rrsm-photo-badge">
                                    <FiCheck />
                                    <span>Custom</span>
                                  </div>
                                )}
                                
                                {/* Loading overlay */}
                                {(photoUploading || photoDeleting) && (
                                  <div className="rrsm-photo-loading">
                                    <motion.span
                                      animate={{ rotate: 360 }}
                                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                    >
                                      <FiLoader />
                                    </motion.span>
                                    <span>{photoUploading ? "Uploading..." : "Removing..."}</span>
                                  </div>
                                )}
                              </div>
                              
                              {/* Photo Actions */}
                              <div className="rrsm-photo-actions">
                                <motion.button
                                  type="button"
                                  className="rrsm-photo-btn rrsm-photo-btn--upload"
                                  onClick={() => photoInputRef.current?.click()}
                                  disabled={photoUploading || photoDeleting}
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                >
                                  <FiUpload />
                                  <span>Upload Photo</span>
                                </motion.button>
                                
                                {room.profilePhoto && (
                                  <motion.button
                                    type="button"
                                    className="rrsm-photo-btn rrsm-photo-btn--delete"
                                    onClick={handlePhotoDelete}
                                    disabled={photoUploading || photoDeleting}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                  >
                                    <FiTrash2 />
                                    <span>Remove</span>
                                  </motion.button>
                                )}
                                
                                <input
                                  ref={photoInputRef}
                                  type="file"
                                  accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                                  className="rrsm-photo-input"
                                  onChange={(e) => e.target.files?.[0] && handlePhotoUpload(e.target.files[0])}
                                />
                              </div>
                              
                              <div className="rrsm-photo-info">
                                <FiInfo />
                                <span>
                                  {room.profilePhoto 
                                    ? "Custom photo uploaded. Remove to use field-based default."
                                    : "Upload a custom photo or use the default based on your field selection."
                                  }
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Save Actions */}
                        <div className="rrsm-form-actions">
                          <motion.button
                            className="rrsm-btn-secondary"
                            onClick={handleReset}
                            disabled={!hasChanges || saving}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <FiRefreshCw />
                            <span>Reset</span>
                          </motion.button>
                          <motion.button
                            className="rrsm-btn-primary"
                            onClick={handleSave}
                            disabled={!hasChanges || saving}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            {saving ? (
                              <motion.span
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                style={{ display: "inline-flex" }}
                              >
                                <FiLoader />
                              </motion.span>
                            ) : (
                              <FiSave />
                            )}
                            <span>{saving ? "Saving..." : "Save Changes"}</span>
                          </motion.button>
                        </div>
                      </motion.div>
                    )}

                    {/* Members Tab */}
                    {activeTab === "members" && (
                      <motion.div
                        className="rrsm-section"
                        variants={sectionVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <SectionHeader 
                          icon={FiUsers} 
                          title="Lab Members" 
                          badge={`${memberCount}/${maxMembers}`}
                          color="green"
                        />

                        {isRoomFull && (
                          <div className="rrsm-alert rrsm-alert--warning">
                            <FiAlertTriangle />
                            <span>Lab is at maximum capacity (8 members)</span>
                          </div>
                        )}

                        <div className="rrsm-members-list">
                          {/* Leader */}
                          <MemberCard
                            member={{ user: room.leader }}
                            isLeader={true}
                            canRemove={false}
                            loading={actionLoading}
                          />
                          
                          {/* Other Members */}
                          {room.members
                            ?.filter((m) => {
                              const mId = m.user?._id || m.user?.id;
                              const leaderId = room.leader?._id || room.leader?.id;
                              return mId !== leaderId;
                            })
                            .map((member) => {
                              const mId = member.user?._id || member.user?.id;
                              return (
                                <MemberCard
                                  key={mId}
                                  member={member}
                                  isLeader={false}
                                  canRemove={true}
                                  onRemove={handleRemoveMember}
                                  loading={actionLoading}
                                />
                              );
                            })}
                        </div>
                      </motion.div>
                    )}

                    {/* Requests Tab */}
                    {activeTab === "requests" && (
                      <motion.div
                        className="rrsm-section"
                        variants={sectionVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <SectionHeader 
                          icon={FiUserPlus} 
                          title="Join Requests" 
                          badge={room.joinRequests?.length || 0}
                          color="cyan"
                        />

                        {room.joinRequests?.length > 0 ? (
                          <div className="rrsm-requests-list">
                            {room.joinRequests.map((request) => (
                              <JoinRequestCard
                                key={request.user?._id || request.user?.id}
                                request={request}
                                onAccept={handleAcceptRequest}
                                onReject={handleRejectRequest}
                                loading={actionLoading}
                                isRoomFull={isRoomFull}
                              />
                            ))}
                          </div>
                        ) : (
                          <div className="rrsm-empty">
                            <FiUserPlus />
                            <p>No pending join requests</p>
                          </div>
                        )}
                      </motion.div>
                    )}

                    {/* Danger Tab */}
                    {activeTab === "danger" && (
                      <motion.div
                        className="rrsm-section"
                        variants={sectionVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <SectionHeader icon={FiTrash2} title="Danger Zone" color="red" />
                        
                        <div className="rrsm-danger-zone">
                          <div className="rrsm-danger-card">
                            <div className="rrsm-danger-header">
                              <FiAlertTriangle className="rrsm-danger-icon" />
                              <h4>Delete this lab</h4>
                            </div>
                            <p className="rrsm-danger-text">
                              Once you delete a lab, there is no going back. All messages, 
                              resources, tasks, and files will be permanently deleted.
                            </p>
                            <motion.button
                              className="rrsm-btn-danger"
                              onClick={handleDeleteRoom}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <FiTrash2 />
                              <span>Delete Lab</span>
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </>
                )}

                {/* ====================== */}
                {/* MEMBER VIEW */}
                {/* ====================== */}
                {!isLeader && (
                  <motion.div
                    className="rrsm-section"
                    variants={sectionVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <SectionHeader icon={FiUsers} title="Your Membership" color="blue" />
                    
                    <div className="rrsm-membership-info">
                      <div className="rrsm-membership-stat">
                        <span className="rrsm-membership-label">Member Since</span>
                        <span className="rrsm-membership-value">
                          {getMemberJoinDate() 
                            ? formatTimeAgo(getMemberJoinDate()) 
                            : "Unknown"}
                        </span>
                      </div>
                      <div className="rrsm-membership-stat">
                        <span className="rrsm-membership-label">Role</span>
                        <span className="rrsm-membership-value">Member</span>
                      </div>
                      <div className="rrsm-membership-stat">
                        <span className="rrsm-membership-label">Lab</span>
                        <span className="rrsm-membership-value">{room.name}</span>
                      </div>
                    </div>

                    <div className="rrsm-leave-section">
                      <div className="rrsm-leave-warning">
                        <FiInfo />
                        <span>
                          Leaving this lab will remove your access to all resources. 
                          You can request to join again later.
                        </span>
                      </div>
                      <motion.button
                        className="rrsm-btn-leave"
                        onClick={handleLeaveRoom}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <FiLogOut />
                        <span>Leave Lab</span>
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>

          {/* Confirmation Modals */}
          <ConfirmModal
            isOpen={confirmModal.isOpen && confirmModal.type === "remove"}
            onClose={closeConfirmModal}
            onConfirm={confirmRemoveMember}
            title={`Remove ${confirmModal.data?.memberName || "member"}?`}
            message="This will remove them from the lab. They can request to join again."
            confirmText="Remove"
            variant="danger"
            loading={actionLoading}
          />

          <ConfirmModal
            isOpen={confirmModal.isOpen && confirmModal.type === "leave"}
            onClose={closeConfirmModal}
            onConfirm={confirmLeaveRoom}
            title={`Leave ${room.name}?`}
            message="You will lose access to all lab resources. You can request to join again later."
            confirmText="Leave Lab"
            variant="warning"
            loading={actionLoading}
          />

          <ConfirmModal
            isOpen={confirmModal.isOpen && confirmModal.type === "delete"}
            onClose={closeConfirmModal}
            onConfirm={confirmDeleteRoom}
            title={`Delete ${room.name}?`}
            message="This action cannot be undone. All data will be permanently deleted including all messages, resources, tasks, and calendar events."
            confirmText="Delete Permanently"
            variant="danger"
            requireInput={room.name}
            inputPlaceholder="Type lab name to confirm"
            loading={actionLoading}
          />
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default RoomSettingsModal;
